import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, HelpCircle } from 'lucide-react';

interface HelpModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                    >
                        <div className="bg-navy-900/95 border-2 border-gold-500/30 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-[0_0_40px_rgba(234,179,8,0.3)] pointer-events-auto">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-gold-600/20 to-gold-500/10 p-6 border-b border-gold-500/20 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <HelpCircle className="w-6 h-6 text-gold-400" />
                                    <h2 className="text-2xl font-bold text-gold-100 font-serif">Règles du Jeu</h2>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="text-gold-400 hover:text-gold-100 transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)] scrollbar-thin scrollbar-thumb-gold-500/30 scrollbar-track-transparent">
                                <div className="space-y-6 text-gold-100/90">
                                    {/* Objectif */}
                                    <section>
                                        <h3 className="text-xl font-bold text-gold-400 mb-3 font-serif">🎯 Objectif</h3>
                                        <p className="leading-relaxed">
                                            Sondez les esprits de vos adversaires pour reconstituer leurs <strong>souvenirs secrets</strong> avant qu'ils ne découvrent les vôtres.
                                            Le premier à atteindre <strong>100 points de Lucidité</strong> remporte la partie !
                                        </p>
                                    </section>

                                    {/* Déroulement */}
                                    <section>
                                        <h3 className="text-xl font-bold text-gold-400 mb-3 font-serif">⚙️ Déroulement d'un Tour</h3>
                                        <ol className="space-y-2 list-decimal list-inside">
                                            <li><strong>Sondez</strong> : Cliquez sur une case dans le Cortex d'un adversaire</li>
                                            <li><strong>Résultat</strong> :
                                                <ul className="ml-6 mt-1 space-y-1 list-disc list-inside text-sm">
                                                    <li><span className="text-cyan-400">🌊 Vide</span> : Rien trouvé, fin du tour</li>
                                                    <li><span className="text-amber-400">✨ Fragment</span> : Vous révélez un mot du souvenir (+5 pts)</li>
                                                    <li><span className="text-red-400">🌑 Zone d'Ombre</span> : Piège ! Pénalité</li>
                                                </ul>
                                            </li>
                                            <li><strong>Souvenir Complet</strong> : Quand tous les fragments sont révélés, répondez à la question finale</li>
                                            <li><strong>Verdict</strong> : Le propriétaire du souvenir valide votre réponse (+25 pts si correct, -5 si faux)</li>
                                        </ol>
                                    </section>

                                    {/* Règles Importantes */}
                                    <section>
                                        <h3 className="text-xl font-bold text-gold-400 mb-3 font-serif">⚠️ Règles Importantes</h3>
                                        <ul className="space-y-2 list-disc list-inside">
                                            <li><strong>Règle d'Équité</strong> : Vous ne pouvez pas cibler le même adversaire deux tours de suite</li>
                                            <li><strong>Grille 7x7</strong> : Chaque joueur possède un Cortex de 49 cases</li>
                                            <li><strong>3 Souvenirs</strong> : Chaque joueur cache 3 souvenirs dans son Cortex</li>
                                            <li><strong>2 Zones d'Ombre</strong> : Pièges défensifs placés stratégiquement</li>
                                        </ul>
                                    </section>

                                    {/* Stratégie */}
                                    <section>
                                        <h3 className="text-xl font-bold text-gold-400 mb-3 font-serif">🧠 Conseils Stratégiques</h3>
                                        <ul className="space-y-2 list-disc list-inside text-sm">
                                            <li>Utilisez le <strong>motif en damier</strong> pour maximiser vos chances de toucher</li>
                                            <li>Une fois qu'un fragment est révélé, <strong>sondez les cases adjacentes</strong></li>
                                            <li>Placez vos Zones d'Ombre près de vos souvenirs pour <strong>protéger</strong> vos secrets</li>
                                            <li>Observez le <strong>log d'actions</strong> pour déduire les positions des souvenirs adverses</li>
                                        </ul>
                                    </section>

                                    {/* Victoire */}
                                    <section>
                                        <h3 className="text-xl font-bold text-gold-400 mb-3 font-serif">🏆 Conditions de Victoire</h3>
                                        <p className="leading-relaxed">
                                            Le premier joueur à atteindre <strong>100 points de Lucidité</strong> est déclaré <strong>Maître de la Mémoire</strong> et remporte la partie.
                                        </p>
                                    </section>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="bg-gradient-to-r from-gold-600/10 to-gold-500/5 p-4 border-t border-gold-500/20 flex justify-end">
                                <button
                                    onClick={onClose}
                                    className="px-6 py-2 bg-gradient-to-r from-gold-600 to-gold-500 text-navy-950 rounded-lg font-bold hover:shadow-[0_0_20px_rgba(234,179,8,0.5)] transition-all"
                                >
                                    Compris !
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default HelpModal;
